// Der Laufzustand einer Aufgabe aus dem Homelab-Protokoll (Topic `task.lauf`,
// docs/protokoll.md › „Tasks über das Protokoll“) in die Form, die die
// Aufgabenliste aus der REST-Antwort kennt (TaskItem.last_run, next_run,
// typical_duration_s).
//
// Rein, ohne Runen und ohne Laufzeit-Importe, damit node (Test
// lib/tasks-lauf-protokoll.test.mjs) sie laden kann: der Stand kommt schon
// skaliert (`skaliere("task.lauf", …)`), die Zeit-Umrechnung (`isoAusMs` aus
// protokoll/werte.ts) als Argument.

import type { LaufStatus, TaskLauf } from "./protokoll/schema.gen";
import type { RunRecord, TaskItem } from "./types";

/** Unix-Millisekunden → ISO-Text (undefined, wenn die Zahl fehlt). */
export type IsoAusMs = (ms: unknown) => string | undefined;

/** „task.<id>.lauf“ → <id>; alles andere → undefined. */
export function taskIdAusInstanz(instanz: string): string | undefined {
  if (!instanz.startsWith("task.") || !instanz.endsWith(".lauf")) return undefined;
  const id = instanz.slice("task.".length, -".lauf".length);
  return id === "" ? undefined : id;
}

/** Aufzählung des Protokolls → Wort der runs-Tabelle (wie die REST-Antwort). */
export function statusWort(s: LaufStatus | null | undefined): string | undefined {
  switch (s) {
    case "laeuft":
      return "running";
    case "ok":
      return "ok";
    case "fehler":
      return "failed";
    case "abgebrochen":
      return "cancelled";
    default:
      return undefined;
  }
}

/** Ist der gemerkte Stand älter als der letzte Lauf einer frischen
 *  REST-Antwort? Dann gilt die Antwort (zum Beispiel nach einer Unterbrechung
 *  des Protokolls: der Stand nennt noch den alten, laufenden Lauf, die Liste
 *  schon den nächsten). Ohne Lauf in der Antwort oder ohne Startzeit im Stand
 *  gilt der Stand. */
export function standAelterAls(t: Pick<TaskItem, "last_run">, d: TaskLauf): boolean {
  const rest = t.last_run?.started_at ? Date.parse(t.last_run.started_at) : NaN;
  if (Number.isNaN(rest) || typeof d.start !== "number") return false;
  return d.start < rest;
}

/** Der letzte Lauf aus dem Stand. `alt` ist der bisherige Eintrag der Liste:
 *  ist es derselbe Lauf, bleiben seine Zusatzfelder (Ausgabe, Ergebnis, Motor,
 *  Rechnerkennung); bei einem anderen Lauf werden Ausgabe und Ergebnis null
 *  (die Liste trägt sie nicht, die Ansicht holt sie je Lauf). Ohne `run` im
 *  Stand gibt es keinen Lauf (null). */
export function lastRunAusLauf(
  aufgabe: Pick<TaskItem, "id" | "name" | "type" | "labels">,
  alt: RunRecord | null,
  d: TaskLauf,
  iso: IsoAusMs,
): RunRecord | null {
  if (!d.run) return null;
  const gleicher = alt?.id === d.run ? alt : null;
  const status = statusWort(d.status) ?? "unknown";
  const fp = d.fortschritt;
  const laeuft = status === "running";
  const lauf: RunRecord = {
    id: d.run,
    task_id: aufgabe.id,
    task_name: aufgabe.name,
    started_at: iso(d.start) ?? gleicher?.started_at ?? "",
    finished_at: iso(d.ende) ?? null,
    status,
    output: gleicher?.output ?? null,
    error: d.fehler ?? null,
    duration_s: typeof d.dauer_ms === "number" ? d.dauer_ms / 1000 : null,
    task_type: alt?.task_type ?? aufgabe.type,
    labels: alt?.labels ?? aufgabe.labels,
    engine: alt?.engine ?? "go",
    result: gleicher?.result ?? null,
    live_progress:
      laeuft && fp
        ? {
            bytes: fp.bytes ?? "",
            pct: fp.pct ?? "",
            speed: fp.speed ?? "",
            eta: fp.eta ?? "",
            updated_at: iso(fp.aktualisiert) ?? "",
          }
        : null,
  };
  return lauf;
}

function gleichesProgress(a: RunRecord["live_progress"], b: RunRecord["live_progress"]): boolean {
  if (!a || !b) return !a && !b;
  return a.bytes === b.bytes && a.pct === b.pct && a.speed === b.speed && a.eta === b.eta && a.updated_at === b.updated_at;
}

function gleicherLauf(a: RunRecord | null, b: RunRecord | null): boolean {
  if (!a || !b) return !a && !b;
  return (
    a.id === b.id &&
    a.status === b.status &&
    a.started_at === b.started_at &&
    a.finished_at === b.finished_at &&
    a.duration_s === b.duration_s &&
    a.error === b.error &&
    gleichesProgress(a.live_progress ?? null, b.live_progress ?? null)
  );
}

/** Die Aufgabe mit dem Laufzustand aus dem Stand: last_run, next_run und
 *  typical_duration_s. Hat sich nichts geändert, kommt dieselbe Aufgabe
 *  (dasselbe Objekt) zurück. */
export function taskMitLauf(t: TaskItem, d: TaskLauf, iso: IsoAusMs): TaskItem {
  const lastRun = lastRunAusLauf(t, t.last_run, d, iso);
  const nextRun = iso(d.naechster) ?? null;
  const typisch = typeof d.typische_dauer_ms === "number" ? d.typische_dauer_ms / 1000 : null;
  const gleichLauf = gleicherLauf(t.last_run, lastRun);
  if (gleichLauf && t.next_run === nextRun && (t.typical_duration_s ?? null) === typisch) return t;
  return {
    ...t,
    // Gleicher Lauf, nichts Neues: das alte Objekt behalten (Referenzgleichheit
    // für alles, was an last_run hängt).
    last_run: gleichLauf ? t.last_run : lastRun,
    next_run: nextRun,
    typical_duration_s: typisch,
  };
}

/** Muss die Liste der offenen Fehlschläge (`/tasks/failing`) neu geladen
 *  werden? Ja, wenn ein Lauf neu auf „failed“ steht, oder wenn ein Lauf ok
 *  endet, während für die Aufgabe ein Fehlschlag offen ist (er heilt dann). */
export function failingNeuLaden(alt: RunRecord | null, neu: RunRecord | null, fehlerOffen: boolean): boolean {
  if (!neu) return false;
  if (neu.status === "failed") return alt?.id !== neu.id || alt.status !== "failed";
  if (neu.status === "ok") return fehlerOffen && (alt?.id !== neu.id || alt.status !== "ok");
  return false;
}
